import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ClipboardList, Printer, Trash2, X, Wand2, Clock, FileDown, Loader2 } from "lucide-react";
import { CILJ_MINUTA, badgeZaPodrucje, DANI } from "@/lib/constants";
import { toast } from "sonner";

export default function PlannerBar({ weekPlan, activeDay, setActiveDay, pulseKey, onRemove, onClearDay, onAutoFill, onPrint }) {
  const [open, setOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const plan = weekPlan[activeDay];
  const totalMin = plan.reduce((s, p) => s + p.trajanje, 0);
  const weekCount = DANI.reduce((s, d) => s + weekPlan[d.id].length, 0);
  const dayLabel = DANI.find((d) => d.id === activeDay).label;
  const pct = Math.min(100, (totalMin / CILJ_MINUTA) * 100);
  const inRange = totalMin >= 90 && totalMin <= CILJ_MINUTA;
  const over = totalMin > CILJ_MINUTA;
  const barColor = over ? "bg-orange-500" : inRange ? "bg-emerald-500" : "bg-indigo-600";

  const downloadPdf = async () => {
    setPdfLoading(true);
    const src = document.getElementById("print-plan");
    const wrapper = document.createElement("div");
    wrapper.style.cssText = "position:absolute;left:-10000px;top:0;width:794px;background:#ffffff;";
    const clone = src.cloneNode(true);
    clone.removeAttribute("id");
    clone.style.cssText = "display:block;width:794px;background:#ffffff;color:#0f172a;font-family:'Figtree',sans-serif;";
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      await html2pdf()
        .set({
          margin: [12, 12, 14, 12],
          filename: "boravko-tjedni-plan.pdf",
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(clone)
        .save();
      toast.success("PDF plan preuzet!");
    } catch (e) {
      toast.error("Greška pri izradi PDF-a.");
    } finally {
      wrapper.remove();
      setPdfLoading(false);
    }
  };

  const dayTabs = (prefix) => (
    <div className="flex gap-1.5">
      {DANI.map((d) => {
        const has = weekPlan[d.id].length > 0;
        const active = activeDay === d.id;
        return (
          <button
            key={d.id}
            data-testid={`${prefix}-day-tab-${d.id}`}
            onClick={() => setActiveDay(d.id)}
            className={`relative rounded-full px-3 py-1.5 text-xs font-bold transition-colors duration-150 ${
              active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {d.kratko}
            {has && <span className={`absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ${active ? "bg-emerald-400" : "bg-indigo-500"}`} />}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md" data-testid="planner-bar">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
        <div className="hidden md:block">{dayTabs("bar")}</div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between text-xs font-semibold mb-1.5">
            <span className="text-slate-600 flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5" />
              {dayLabel} · {plan.length} {plan.length === 1 ? "aktivnost" : "aktivnosti"}
            </span>
            <span data-testid="planner-total" className={over ? "text-orange-600" : inRange ? "text-emerald-600" : "text-slate-600"}>
              {totalMin} / {CILJ_MINUTA} min {over && "· preko 2 sata!"} {inRange && "· taman!"}
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div key={pulseKey} className={`h-full rounded-full ${barColor} planner-pulse transition-transform duration-300`} style={{ width: `${pct}%` }} />
          </div>
        </div>

        <Button data-testid="autofill-btn" onClick={onAutoFill} variant="outline"
          className="rounded-full border-indigo-200 text-indigo-700 hover:bg-indigo-50 hidden sm:inline-flex">
          <Wand2 className="h-4 w-4 mr-1.5" /> Složi mi dan
        </Button>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button data-testid="open-planner-btn" className="rounded-full bg-indigo-600 hover:bg-indigo-700">
              Tjedni plan{weekCount > 0 ? ` (${weekCount})` : ""}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="planner-sheet">
            <SheetHeader className="text-left">
              <SheetTitle className="font-display text-xl font-black">Tjedni plan slobodnog vremena</SheetTitle>
              <SheetDescription>
                Svaki dan ima dva puna sunčana sata, dakle {CILJ_MINUTA} minuta. Plan se ne sprema, pa ga isprintajte ili preuzmite PDF prije zatvaranja stranice.
              </SheetDescription>
            </SheetHeader>

            <div className="mt-5">{dayTabs("sheet")}</div>

            <div className="mt-4 space-y-3">
              {plan.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-10" data-testid="planner-empty">
                  {dayLabel} je prazan. Dodajte aktivnosti klikom na &bdquo;Dodaj u plan&rdquo; ili upotrijebite &bdquo;Složi mi dan&rdquo;.
                </p>
              )}
              {plan.map((p, i) => (
                <div key={p.planId} data-testid={`plan-item-${p.planId}`}
                  className="rounded-xl border border-slate-200 bg-white p-3.5 flex items-start gap-3">
                  <span className="h-6 w-6 shrink-0 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 leading-tight">{p.naziv}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badgeZaPodrucje(p.podrucje)}`}>{p.podrucje}</span>
                      <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3 w-3" />{p.trajanje} min</span>
                    </div>
                  </div>
                  <button data-testid={`remove-plan-item-${p.planId}`} onClick={() => onRemove(activeDay, p.planId)}
                    className="text-slate-400 hover:text-red-500 transition-colors duration-150">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-2.5">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5 flex items-center justify-between text-sm font-bold">
                <span className="text-slate-600">{dayLabel} ukupno</span>
                <span className={over ? "text-orange-600" : inRange ? "text-emerald-600" : "text-slate-900"}>{totalMin} min</span>
              </div>
              {weekCount > 0 && (
                <>
                  <Button data-testid="pdf-plan-btn" onClick={downloadPdf} disabled={pdfLoading}
                    className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 py-5 font-bold">
                    {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
                    {pdfLoading ? "Izrađujem PDF…" : "Preuzmi PDF (cijeli tjedan)"}
                  </Button>
                  <Button data-testid="print-plan-btn" onClick={onPrint} variant="outline"
                    className="w-full rounded-full border-indigo-200 text-indigo-700 hover:bg-indigo-50 py-5 font-bold">
                    <Printer className="h-4 w-4 mr-2" /> Isprintaj plan
                  </Button>
                </>
              )}
              {plan.length > 0 && (
                <Button data-testid="clear-plan-btn" onClick={onClearDay} variant="ghost"
                  className="w-full rounded-full text-slate-500 hover:text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" /> Isprazni {dayLabel.toLowerCase()}
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
